import { Action, ActionStats, DbStatus } from '../types/Action';

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const README_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface ReadmeCacheEntry {
  content: string | null;
  cachedAt: number;
}

function getApiBaseUrl(): string {
  const fallback = '/api';
  // Optional chaining on `env` keeps this safe when the module is imported outside of
  // Vite (e.g. by the Playwright test runner), where `import.meta.env` is undefined.
  const configured = import.meta.env?.VITE_API_BASE_URL;

  if (!configured) {
    return fallback;
  }

  const trimmed = String(configured).trim();
  if (!trimmed) {
    return fallback;
  }

  // If someone accidentally configures the API base as the frontend origin
  // (e.g. `http://localhost:3000/`), requests like `/actions/list` will hit the
  // SPA dev server and return HTML. In that case, use the dev proxy path.
  try {
    const resolved = new URL(trimmed, window.location.origin);
    if (resolved.origin === window.location.origin) {
      return fallback;
    }

    return resolved.toString().replace(/\/+$/, '');
  } catch {
    // If it's not a URL, assume it's a path like `/api`.
    return trimmed.replace(/\/+$/, '') || fallback;
  }
}

const API_BASE_URL = getApiBaseUrl();

// Parses a dependents count string that may end with "+" (e.g. "999+").
// Returns a numeric value suitable for sorting; "999+" sorts above "999".
export function parseDependentsCount(value: string | undefined): number {
  if (!value) return 0;
  const isPlus = value.endsWith('+');
  // Values may be comma-formatted (e.g. "15,356,161"); parseInt stops at the
  // first non-digit character, so strip thousands separators first or large
  // counts get silently truncated (e.g. "16,842,392" -> 16).
  const digitsOnly = value.replace(/,/g, '');
  const num = parseInt(digitsOnly, 10);
  if (!Number.isFinite(num)) return 0;
  // Fractional bump ensures "999+" sorts strictly above "999".
  return isPlus ? num + 0.5 : num;
}

// Formats a dependents count string for display, preserving any "+" suffix.
export function formatDependentsCount(value: string | undefined): string {
  if (!value) return '0';
  const isPlus = value.endsWith('+');
  // Strip thousands separators before parsing; see parseDependentsCount.
  const digitsOnly = value.replace(/,/g, '');
  const num = parseInt(digitsOnly, 10);
  if (!Number.isFinite(num)) return '0';
  return isPlus ? `${num.toLocaleString()}+` : num.toLocaleString();
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const result: string[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      result.push(item);
      continue;
    }

    if (item && typeof item === 'object') {
      const tagName = (item as { tag_name?: unknown }).tag_name;
      if (typeof tagName === 'string' && tagName.trim()) {
        result.push(tagName);
        continue;
      }
    }
  }

  return result;
}

function normalizeAction(raw: unknown): Action {
  const action = (raw && typeof raw === 'object') ? (raw as any) : {};

  // The production dataset can contain GitHub release objects; UI expects strings.
  const releaseInfo = normalizeStringArray(action.releaseInfo);
  const tagInfo = normalizeStringArray(action.tagInfo);

  // Be defensive with a couple of frequently read fields.
  const dependents = action.dependents && typeof action.dependents === 'object'
    ? action.dependents
    : { dependents: '0', dependentsLastUpdated: '' };

  if (dependents && typeof dependents.dependents === 'number') {
    dependents.dependents = String(dependents.dependents);
  }

  // Ensure repoInfo always exists with sensible defaults
  const repoInfo = action.repoInfo && typeof action.repoInfo === 'object'
    ? action.repoInfo
    : {
        latest_release_published_at: '',
        disabled: false,
        updated_at: '',
        archived: false
      };

  // Normalize OpenSSF score: support incoming snake_case `openssf_score` or camel `ossfScore`.
  const rawScore = action && typeof action === 'object' ? (action.openssf_score ?? action.ossfScore ?? action.ossf_score ?? null) : null;
  let parsedScore: number | null = null;
  if (typeof rawScore === 'string') {
    const p = parseFloat(rawScore);
    parsedScore = Number.isFinite(p) ? p : null;
  } else if (typeof rawScore === 'number' && Number.isFinite(rawScore)) {
    parsedScore = rawScore;
  }

  // `ossf` must reflect the raw flag exactly: it is only ever set to `true` by
  // the crawler when a Scorecard result was actually loaded. Synthesizing it
  // from "any legacy score field is present" would re-introduce the same
  // overcount bug that was just fixed on the backend (a leftover/stale score
  // field would get counted as "found" again).
  const ossf = action.ossf === true;
  const ossfScore = parsedScore !== null ? parsedScore : (typeof action.ossfScore === 'number' ? action.ossfScore : 0);

  return {
    ...action,
    releaseInfo,
    tagInfo,
    dependents,
    repoInfo,
    ossf,
    ossfScore
  } as Action;
}

function extractArrayFromUnknown(value: unknown): unknown[] {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return extractArrayFromUnknown(parsed);
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  const obj = value as Record<string, unknown>;

  // Common wrapper shapes.
  const preferredKeys = ['items', 'value', 'actions', 'results', 'data', 'body'];
  for (const key of preferredKeys) {
    const candidate = obj[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (key === 'body' && typeof candidate === 'string') {
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // ignore
      }
    }
  }

  // Last resort: find the first array-valued property.
  for (const candidate of Object.values(obj)) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}

export class ActionsService {
  private actions: Action[] = [];
  private loading: boolean = false;
  private lastFetch: number = 0;
  private listeners: Array<() => void> = [];
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private stats: ActionStats = { total: 0, byType: {}, verified: 0, archived: 0, withOssf: 0 };
  private lastStatsFetch: number = 0;
  private inFlightActionsFetch: Promise<Action[]> | null = null;
  private inFlightStatsFetch: Promise<ActionStats> | null = null;
  private inFlightDbStatusFetch: Promise<DbStatus> | null = null;
  private readmeCache: Map<string, ReadmeCacheEntry> = new Map();
  private snapshotGeneratedAt: string | null = null;

  constructor() {
    this.startAutoRefresh();
  }

  private startAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
    this.refreshTimer = setInterval(() => {
      this.fetchActions(true);
    }, REFRESH_INTERVAL);
  }

  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach(listener => listener());
  }

  // Fetches the precomputed snapshot: every action the list pages need, in one
  // request, already sorted newest-first.
  //
  // Uses the default HTTP cache rather than `no-store` so the endpoint's
  // Cache-Control/ETag actually do something — a repeat visit inside the
  // freshness window costs no network at all, and outside it costs a 304.
  private async fetchSnapshot(): Promise<Action[] | null> {
    const response = await fetch(`${API_BASE_URL}/actions/snapshot`);

    if (response.status === 503) {
      // No snapshot built yet (fresh environment, or the pipeline has never
      // completed). Signal the caller to fall back rather than showing an error.
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch actions snapshot: ${response.statusText}`);
    }

    const data = await response.json();
    const items = extractArrayFromUnknown(data);
    this.snapshotGeneratedAt = (data && typeof data === 'object' && typeof data.generatedAt === 'string')
      ? data.generatedAt
      : null;

    return items.map(normalizeAction);
  }

  async fetchActions(force: boolean = false): Promise<Action[]> {
    const now = Date.now();

    if (!force && this.actions.length > 0 && (now - this.lastFetch) < REFRESH_INTERVAL) {
      return this.actions;
    }

    if (this.inFlightActionsFetch) {
      return await this.inFlightActionsFetch;
    }

    this.loading = true;

    const fetchPromise = (async () => {
      try {
        let items = await this.fetchSnapshot();

        if (items === null) {
          // Fall back to the full list scan. Slow (~50s over ~35k actions) but
          // correct, so the site still works before the first snapshot exists.
          console.warn('Actions snapshot unavailable; falling back to the full /actions/list scan.');
          const response = await fetch(`${API_BASE_URL}/actions/list`, { cache: 'no-store' });
          if (!response.ok) {
            throw new Error(`Failed to fetch actions: ${response.statusText}`);
          }

          const data = await response.json();
          const fallbackItems = extractArrayFromUnknown(data);
          const hasCountHeader = response.headers.has('x-actions-count');
          const declaredCount = Number(response.headers.get('x-actions-count') || '0');

          const serverExplicitlyEmpty =
            (hasCountHeader && declaredCount === 0) ||
            (Array.isArray(data) && data.length === 0);

          // If the server claims there are results but we couldn't extract them,
          // keep any previously cached actions instead of wiping the UI.
          if (fallbackItems.length === 0 && this.actions.length > 0 && !serverExplicitlyEmpty) {
            const responseType = Array.isArray(data) ? 'array' : typeof data;
            const keys = (data && typeof data === 'object' && !Array.isArray(data))
              ? Object.keys(data)
              : [];
            console.warn(
              `Actions list response parsed to 0 items; keeping cached actions. responseType=${responseType}, keys=${keys.join(',')}`
            );
            items = this.actions;
          } else {
            items = fallbackItems.map(normalizeAction);
          }
        }

        this.actions = items;
        this.lastFetch = now;
        this.notify();
        return this.actions;
      } catch (error) {
        console.error('Error fetching actions:', error);
        throw error;
      } finally {
        this.loading = false;
        this.inFlightActionsFetch = null;
      }
    })();

    this.inFlightActionsFetch = fetchPromise;
    return await fetchPromise;
  }

  // ISO timestamp of when the served snapshot was built, or null when the
  // data came from the live-scan fallback.
  getSnapshotGeneratedAt(): string | null {
    return this.snapshotGeneratedAt;
  }

  async fetchActionsPage(limit: number): Promise<Action[]> {
    if (!Number.isFinite(limit) || limit <= 0) {
      throw new Error('Invalid limit parameter: must be a positive number');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/actions/list?limit=${limit}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Failed to fetch actions page: ${response.statusText}`);
      }

      const data = await response.json();
      const items = extractArrayFromUnknown(data);
      return items.map(normalizeAction);
    } catch (error) {
      console.error('Error fetching actions page:', error);
      throw error;
    }
  }

  async fetchStats(force: boolean = false): Promise<ActionStats> {
    const now = Date.now();
    if (!force && this.stats.total > 0 && (now - this.lastStatsFetch) < REFRESH_INTERVAL) {
      return this.stats;
    }

    if (this.inFlightStatsFetch) {
      return await this.inFlightStatsFetch;
    }

    const fetchPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/actions/stats`, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Failed to fetch stats: ${response.statusText}`);
        }

        const data = await response.json();
        const stats: ActionStats = {
          total: Number(data?.total) || 0,
          byType: data?.byType || {},
          verified: Number(data?.verified) || 0,
          archived: Number(data?.archived) || 0,
          withOssf: Number(data?.withOssf) || 0
        };
        this.stats = stats;
        this.lastStatsFetch = now;
        this.notify();
        return stats;
      } catch (error) {
        console.error('Error fetching stats:', error);
        throw error;
      } finally {
        this.inFlightStatsFetch = null;
      }
    })();

    this.inFlightStatsFetch = fetchPromise;
    return await fetchPromise;
  }

  getActions(): Action[] {
    return this.actions;
  }

  getAction(owner: string, name: string): Action | undefined {
    return this.actions.find(
      action => action.owner.toLowerCase() === owner.toLowerCase() && 
                action.name.toLowerCase() === name.toLowerCase()
    );
  }

  async fetchActionDetail(owner: string, name: string): Promise<Action | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/actions/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
        { cache: 'no-store' }
      );
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch action detail: ${response.statusText}`);
      }

      const data = await response.json();
      return normalizeAction(data);
    } catch (error) {
      console.error('Error fetching action detail:', error);
      throw error;
    }
  }

  isLoading(): boolean {
    return this.loading;
  }

  getStats() {
    return this.stats;
  }

  async fetchReadme(owner: string, name: string, version?: string): Promise<string | null> {
    const cacheKey = `${owner.toLowerCase()}/${name.toLowerCase()}@${version ?? ''}`;
    const cached = this.readmeCache.get(cacheKey);
    if (cached && (Date.now() - cached.cachedAt) < README_TTL_MS) {
      return cached.content;
    }

    try {
      const versionParam = version ? `?version=${encodeURIComponent(version)}` : '';
      const response = await fetch(
        `${API_BASE_URL}/actions/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/readme${versionParam}`,
        { cache: 'no-store' }
      );
      if (!response.ok) {
        if (response.status === 404) {
          this.readmeCache.set(cacheKey, { content: null, cachedAt: Date.now() });
          return null;
        }
        throw new Error(`Failed to fetch README: ${response.statusText}`);
      }

      const content = await response.text();
      this.readmeCache.set(cacheKey, { content, cachedAt: Date.now() });
      return content;
    } catch (error) {
      console.error('Error fetching README:', error);
      throw error;
    }
  }

  async fetchDbStatus(): Promise<DbStatus> {
    if (this.inFlightDbStatusFetch) {
      return await this.inFlightDbStatusFetch;
    }

    const fetchPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/actions/status`, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Failed to fetch DB status: ${response.statusText}`);
        }
        return await response.json() as DbStatus;
      } catch (error) {
        console.error('Error fetching DB status:', error);
        throw error;
      } finally {
        this.inFlightDbStatusFetch = null;
      }
    })();

    this.inFlightDbStatusFetch = fetchPromise;
    return await fetchPromise;
  }

  destroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
    this.listeners = [];
    this.readmeCache.clear();
  }
}

export const actionsService = new ActionsService();
